import React from 'react';
import { Badge, Button } from '@ygtechnologies/rubus-ui';
import { faAngleLeft, faLayerGroup } from '@fortawesome/pro-light-svg-icons';
import { EmailContentOverride, EmailLayoutOverride } from '@/services/emailTemplates';
import { languageLabel, sortOverrides } from './shared';
import theme from './EmailTemplates.module.scss';

type AnyOverride = EmailContentOverride | EmailLayoutOverride;

interface OverridesSummaryProps {
    overrides: AnyOverride[];
    /** Highlights the row matching the current context. */
    activeId: number | null;
    onSelect(override: AnyOverride): void;
    /** Collapses the sidebar; reopening happens via ShowOverridesButton in the editor header. */
    onHide(): void;
}

/**
 * Reopens the collapsed sidebar. Sits in front of the editor's source note rather than in a rail of its
 * own so a collapsed sidebar costs no horizontal space at all.
 */
export const ShowOverridesButton: React.FC<{ count: number; onClick(): void }> = ({ count, onClick }) => (
    <Button size="xs" variant="tertiary" icon={faLayerGroup} onClick={onClick} title={`Show overrides (${count})`}>
        {count}
    </Button>
);

/**
 * Sidebar listing every override of this template, so they stay discoverable now that scope lives in the
 * bar — without it you could set a context, save, and never notice you had shadowed an existing override.
 * Clicking one moves the bar to that scope.
 *
 * A list rather than a row of chips because the count is unbounded: a seller profile with twenty
 * overridden projects would otherwise wrap into a block that pushes the editor down the page. Collapsible
 * so the editor and preview can reclaim the width, remembered like the preview toggle. The open/closed
 * state lives in the page, which renders this only while open.
 */
const OverridesSummary: React.FC<OverridesSummaryProps> = ({ overrides, activeId, onSelect, onHide }) => {
    const projectOverrides = sortOverrides(overrides).filter((x) => x.projectId);
    const profileOverrides = sortOverrides(overrides).filter((x) => !x.projectId);

    const renderGroup = (label: string, rows: AnyOverride[]) =>
        rows.length > 0 && (
            <div className={theme.overridesGroup}>
                <span className={theme.overridesGroupLabel}>{label}</span>
                {rows.map((override) => (
                    <button
                        type="button"
                        key={override.id}
                        className={override.id === activeId ? theme.overrideRowActive : theme.overrideRow}
                        onClick={() => onSelect(override)}>
                        <span className={theme.overrideRowName}>{override.scopeName ?? 'Unknown'}</span>
                        <span className={theme.overrideRowLanguage}>{languageLabel(override.languageCode)}</span>
                    </button>
                ))}
            </div>
        );

    return (
        <aside className={theme.overridesSidebar}>
            <div className={theme.overridesSidebarHeader}>
                <h4 className={theme.overridesSummaryHeader}>
                    Overrides
                    {overrides.length > 0 && (
                        <Badge variant="info" appearance="subtle" size="xs">
                            {overrides.length}
                        </Badge>
                    )}
                </h4>
                <Button size="xs" variant="tertiary" icon={faAngleLeft} onClick={onHide} title="Hide overrides" />
            </div>

            {overrides.length === 0 ? (
                <p className={theme.hint}>None — every project uses the global template.</p>
            ) : (
                <div className={theme.overridesScroll}>
                    {renderGroup('Projects', projectOverrides)}
                    {renderGroup('Seller profiles', profileOverrides)}
                </div>
            )}
        </aside>
    );
};

export default OverridesSummary;
